import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { FiCheck, FiArrowRight, FiServer } from "react-icons/fi";
import api from "../../api/api";

interface PlanRecord {
  id?: number;
  name: string;
  slug: string;
  price_monthly?: string | number | null;
  price_yearly?: string | number | null;
  max_projects?: number | null;
  max_users?: number | null;
  max_storage_mb?: number | null;
  max_invoices_per_month?: number | null;
  features?: any;
}

const formatLimit = (value: number | null | undefined): string =>
  value != null && value > 9999 ? "Unlimited" : String(value ?? 0);

const formatStorage = (mb: number | null | undefined): string => {
  if (mb == null) return "-";
  if (mb > 9999) return "Unlimited";
  return mb >= 1024 ? `${Math.round(mb / 1024)} GB` : `${mb} MB`;
};

const extractPlanFeatures = (plan: PlanRecord): string[] => {
  const features = plan.features;
  if (Array.isArray(features)) {
    return features
      .map((feature) =>
        typeof feature === "string" ? feature : String(feature?.label ?? feature?.name ?? "")
      )
      .filter(Boolean);
  }
  if (features && typeof features === "object") {
    return Object.entries(features)
      .filter(([, enabled]) => enabled === true || typeof enabled === "string")
      .map(([key]) =>
        key.replace(/[_-]+/g, " ").replace(/\b\w/g, (letter) => letter.toUpperCase())
      );
  }
  return [];
};

const Pricing: React.FC = () => {
  const [plans, setPlans] = useState<PlanRecord[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let isMounted = true;
    const fetchPlans = async () => {
      try {
        setLoading(true);
        const res = await api.get("/plans");
        const data = res.data?.data;
        const plansData: PlanRecord[] = Array.isArray(data)
          ? data
          : Array.isArray(data?.data)
          ? data.data
          : [];
        if (isMounted) setPlans(plansData);
      } catch (err: any) {
        if (isMounted)
          setError(err.response?.data?.message || "Failed to load plans. Please try again later.");
      } finally {
        if (isMounted) setLoading(false);
      }
    };
    fetchPlans();
    return () => {
      isMounted = false;
    };
  }, []);

  const planCards = useMemo(
    () =>
      plans.map((plan, idx) => ({
        ...plan,
        displayPrice: `₹ ${new Intl.NumberFormat("en-IN", { maximumFractionDigits: 0 }).format(
          Number(plan.price_monthly || 0)
        )}`,
        popular: plans.length > 1 && idx === Math.min(1, plans.length - 1),
        features: [
          `${formatLimit(plan.max_projects)} Active Projects`,
          `${formatLimit(plan.max_users)} Team User Seats`,
          `${formatLimit(plan.max_invoices_per_month)} Invoices / Month`,
          `${formatStorage(plan.max_storage_mb)} Document Storage`,
          ...extractPlanFeatures(plan),
        ],
      })),
    [plans]
  );

  return (
    <div className="min-h-screen bg-[#0d1512] text-slate-900 font-sans relative overflow-hidden">
      {/* Ambient background glows */}
      <div className="absolute -top-40 -left-40 w-96 h-96 bg-emerald-600/15 rounded-full blur-3xl pointer-events-none" />
      <div className="absolute -bottom-40 -right-40 w-96 h-96 bg-emerald-500/10 rounded-full blur-3xl pointer-events-none" />

      <div className="relative z-10 max-w-6xl mx-auto px-4 sm:px-6 py-14">
        {/* Header */}
        <div className="text-center space-y-3 mb-12">
          <span className="inline-block px-3 py-1 rounded-full text-[11px] font-bold uppercase tracking-widest text-emerald-300 border border-emerald-400/30 bg-emerald-400/10">
            Simple, transparent pricing
          </span>
          <h1 className="text-3xl sm:text-4xl font-black tracking-tight text-white">
            Start your <span className="text-emerald-400">14-day free trial</span>
          </h1>
          <p className="text-sm text-slate-400 max-w-xl mx-auto">
            Full access to the construction operations & commercial suite. No credit card required — pick the plan that fits your business.
          </p>
        </div>

        {error && (
          <div className="max-w-lg mx-auto mb-8 p-4 bg-rose-50 border border-rose-200 text-rose-800 rounded-2xl text-xs font-semibold text-left">
            {error}
          </div>
        )}

        {loading ? (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {[0, 1, 2].map((idx) => (
              <div key={idx} className="rounded-3xl p-8 bg-white border border-slate-200 shadow-sm space-y-4 animate-pulse">
                <div className="cms-skeleton h-5 w-1/2" />
                <div className="cms-skeleton h-9 w-2/3" />
                <div className="cms-skeleton h-3 w-full" />
                <div className="cms-skeleton h-3 w-full" />
                <div className="cms-skeleton h-10 w-full" />
              </div>
            ))}
          </div>
        ) : planCards.length === 0 ? (
          <div className="rounded-3xl p-10 bg-white border border-slate-200 text-center max-w-md mx-auto">
            <FiServer className="mx-auto w-8 h-8 text-slate-300" />
            <p className="mt-3 text-sm font-bold text-slate-700">No plans are available right now</p>
            <p className="text-xs text-slate-400 mt-1">
              Please check back soon or contact our sales team.
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 items-start">
            {planCards.map((plan) => (
              <div
                key={plan.slug}
                className={`relative rounded-3xl p-8 bg-white flex flex-col justify-between min-h-[420px] transition-all duration-300 ${
                  plan.popular
                    ? "border-2 border-indigo-600 shadow-2xl ring-2 ring-indigo-600/20 md:-translate-y-2"
                    : "border border-slate-200 shadow-sm hover:border-slate-300"
                }`}
              >
                {plan.popular && (
                  <div className="absolute -top-3.5 left-1/2 -translate-x-1/2 bg-gradient-to-r from-indigo-600 to-purple-600 text-white text-xs font-bold px-4 py-1 rounded-full shadow-md whitespace-nowrap">
                    Most Popular
                  </div>
                )}

                <div className="space-y-5">
                  <div>
                    <h2 className="text-xl font-black text-slate-900">{plan.name}</h2>
                    <p className="text-slate-500 text-xs mt-1 capitalize">{plan.slug} tier</p>
                  </div>

                  <div className="flex items-baseline gap-1.5">
                    <span className="text-3xl font-black text-slate-900">{plan.displayPrice}</span>
                    <span className="text-xs text-slate-400 font-semibold">/ month</span>
                  </div>
                  <p className="text-[11px] text-slate-400 font-medium">
                    Billed yearly at ₹{new Intl.NumberFormat("en-IN", { maximumFractionDigits: 0 }).format(Number(plan.price_yearly || Number(plan.price_monthly || 0) * 12))}
                  </p>

                  <ul className="space-y-2 pt-2 border-t border-slate-100">
                    {plan.features.map((feature, idx) => (
                      <li key={idx} className="flex items-start gap-2 text-xs text-slate-600">
                        <FiCheck className="w-4 h-4 text-emerald-600 shrink-0 mt-0.5" />
                        <span>{feature}</span>
                      </li>
                    ))}
                  </ul>
                </div>

                <Link
                  to={`/signup?plan=${encodeURIComponent(plan.slug)}`}
                  className={`mt-8 w-full py-3 text-center font-extrabold text-xs rounded-xl transition-all duration-200 inline-flex items-center justify-center gap-2 ${
                    plan.popular
                      ? "bg-indigo-600 hover:bg-indigo-700 text-white shadow-lg shadow-indigo-600/30"
                      : "bg-[#064e3b] hover:bg-[#047857] text-white"
                  }`}
                >
                  Start free trial <FiArrowRight className="w-4 h-4" />
                </Link>
              </div>
            ))}
          </div>
        )}

        {/* Footer */}
        <div className="mt-12 text-center text-[11px] text-slate-500 font-medium">
          Already have an account?{" "}
          <Link to="/login" className="font-bold text-emerald-400 hover:text-emerald-300 transition-colors">
            Sign in
          </Link>
        </div>
      </div>
    </div>
  );
};

export default Pricing;
